/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { resolveRestApi } from '../router/app-router';
import { fetch as axiosFetch } from '../service_broker/fetch';

import type { CalculatedFieldsConfig, CalculatedFieldsResponse, RequestConfig } from './types';

/**
 * The ViewRule module contains APIs to handle performing CRUD operations to View Rules
 * @module ViewRule
 */

const CREATE_VIEW_RULE = 'createViewRuleByID';
const GET_VIEW_RULE = 'getViewRuleByID';
const UPDATE_VIEW_RULE = 'updateViewRuleByID';
const DELETE_VIEW_RULE = 'deleteViewRuleByID';
const GET_CALCULATED_FIELDS_IN_VIEW = 'getCalculatedFieldsInViewByID';

/**
 * This API creates a Rule-UI-View rule instance.
 * @function
 * @static
 * @example <caption>In this example, the API creates a Rule-UI-View record named TestView.</caption>
 *
 * const requestConfig = {
 *    label: "TestView",
 *    className: "Work-",
 *    description: "TestDescription",
 *    category: "TestingViewType",
 *    metadata: '{"name":"TestView","type":"View","config":{"template":"TwoColumn","ruleClass":"Work-,"localeReference":"@LR WORK-!VIEW!TESTVIEW"},"children":[{"name":"A","type":"Region","children":[]},{"name":"B","type":"Region","children":[]}]}',
 *    markAsRelevantRecord: false,
 *    additionalPayload: "{
 *      visibility: "shared",
 *      accessGroup: "AcmeCo:Authors"
 *    }"
 * };
 *
 * const context = "app/primary_1";
 *
 * PCore.getViewRuleApi().createViewRule(requestConfig, context)
 * .then(response => {
 *   // response data (i.e., viewName and updateDateTime in an object)
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param requestConfig Additional request info as needed to be specified as the body
 *   -label {string, required}: Controls Rule-UI-View record name (and short description)
 *   -className {string, required}: The class of the Rule-UI-View record
 *   -description {string}: Sets the pyViewDescription (exposed) property on the Rule-UI-View record
 *   -category {string}: Sets the pxViewType (exposed) property (useful for reporting purposes)
 *   -metadata {stringified JSON, required}: Sets the pxViewMetadata property on the Rule-UI-View record. It represents the underlying view metadata, for generating context metadata and dependencies
 *   -markAsRelevantRecord {boolean}: Controls whether the Rule-UI-View record is a relevant record or not (defaults to false)
 *   -additionalPayload (stringified JSON): Additional JSON: Additional JSON as need to be stored on the Rule-UI-View record
 *     -visibility: Sets the pyVisibilityType (exposed) property, either "public," "private," or "shared,"
 *     -accessGroup: Set on the pyVisibilityAccessGroup (exposed property), represents the allowed access group that other users need to perform CRUD operations on the View (only needed for "shared")
 * @param context The name of the context where the API is being called (e.g., app/primary_1)
 * @returns The Promise that resolves to an object containing the viewName and updateDateTime
 */
const createViewRule = (requestConfig: RequestConfig, context: string) => {
  const { restApi, reqType } = resolveRestApi(CREATE_VIEW_RULE);

  return axiosFetch(
    restApi,
    {
      method: reqType,
      body: requestConfig
    },
    context
  ).then((response: any) => response.data);
};

/**
 * This API gets a Rule-UI-View rule instance.
 * @function
 * @static
 * @example <caption>In this example, the API gets a Rule-UI-View record named TestView.</caption>
 *
 * const ruleName = "TestView";
 * const className = "Work-";
 * const ID = `${className}!${ruleName}`;
 *
 * const context = "app/primary_1";
 *
 * PCore.getViewRuleApi().getViewRule(ID, context)
 * .then(response => {
 *    // response data (i.e., label, view description, category, metadata, class name, ruleset, ruleset version, visibility type, visibility access group, and update date time in an object)
 * }).catch(err => {
 *    // errors
 * });
 *
 * @param ID The insName (i.e., className!ruleName) of the Rule-UI-View record
 * @param context The name of the context where the API is being called (e.g., app/primary_1)
 * @returns The Promise that resolves to an object containing the  label, view description, category, metadata, class name,
 * ruleset, ruleset version, visibility type, visibility access group, and update date time
 */
const getViewRule = (ID: string, context?: string) => {
  const { restApi, reqType } = resolveRestApi(GET_VIEW_RULE, { ID });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  ).then((response: any) => response.data);
};

/**
 * This API updates a Rule-UI-View rule instance.
 * @function
 * @static
 * @example <caption>In this example, the API updates a Rule-UI-View record named TestView.</caption>
 *
 * const ruleName = "TestView";
 * const className = "Work-";
 * const ID = `${className}!${ruleName}`;
 *
 * const requestConfig = {
 *    label: "New TestView",
 *    description: "New TestDescription",
 *    metadata: '{"name":"TestView","type":"View","config":{"template":"TwoColumn","ruleClass":"Work-,"localeReference":"@LR WORK-!VIEW!TESTVIEW"},"children":[{"name":"A","type":"Region","children":[]},{"name":"B","type":"Region","children":[]}]}',
 *    additionalPayload: "{}"
 * };
 *
 * const context = "app/primary_1";
 *
 * PCore.getViewRuleApi().updateViewRule(ID, requestConfig, context)
 * .then(response => {
 *    // response data (i.e., updateDateTime in an object)
 * }).catch(err => {
 *    // errors
 * });
 *
 * @param ID The insName (i.e., className!ruleName) of the Rule-UI-View record
 * @param requestConfig Additional request info as needed to be specified as the body
 *   -label {string}: Controls Rule-UI-View record name (and short description)
 *   -description {string}: Sets the pyViewDescription (exposed) property on the Rule-UI-View record
 *   -metadata {stringified JSON}: Sets the pxViewMetadata property on the Rule-UI-View record. It represents the underlying view metadata, for generating context metadata and dependencies
 *   -additionalPayload (stringified JSON): Additional JSON: Additional JSON as need to be stored on the Rule-UI-View record
 * @param context The name of the context where the API is being called (e.g., app/primary_1)
 * @returns The Promise that resolves to an object containing the updateDateTime
 */
const updateViewRule = (ID: string, requestConfig: RequestConfig, context?: string) => {
  const { restApi, reqType } = resolveRestApi(UPDATE_VIEW_RULE, { ID });

  return axiosFetch(
    restApi,
    {
      method: reqType,
      body: requestConfig
    },
    context
  ).then((response: any) => response.data);
};

/**
 * This API gets a Rule-UI-View rule instance.
 * @function
 * @static
 * @example <caption>In this example, the API deletes a Rule-UI-View record named TestView.</caption>
 *
 * const ruleName = "TestView";
 * const className = "Work-";
 * const ID = `${className}!${ruleName}`;
 *
 * const context = "app/primary_1";
 *
 * PCore.getViewRuleApi().deleteViewRule(ID, context)
 * .then(response => {
 *    // response { status: "SUCCESS" }
 * }).catch(err => {
 *    // errors
 * });
 *
 * @param ID The insName (i.e., className!ruleName) of the Rule-UI-View record
 * @param context The name of the context where the API is being called (e.g., app/primary_1)
 * @returns The Promise that resolves to an object containing the status if the delete was successful
 */
const deleteViewRule = (ID: string, context?: string): Promise<object> => {
  const { restApi, reqType } = resolveRestApi(DELETE_VIEW_RULE, { ID });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  ).then((response: any) => response.data);
};

/**
 * This API gets calculated fields in Rule-UI-View rule instance.
 * @function
 * @static
 * @example <caption>In this example, the API fetches values for
 *  given calculatedFields in a Rule-UI-View record named TestView.</caption>
 *
 * const caseName = "TestCase";
 * const ruleName = "TestView";
 * const className = "Work-";
 * const caseID = `${className}-${caseName}`;
 * const viewID = `${className}!${ruleName}`;
 *
 const fieldsConfig = {
 *    name: "DetailsTabCount",
 *    context: "app/primary_1",
 * };
 *
 * PCore.getViewRuleApi().getCalculatedFields(ID, fieldsConfig)
 * .then(response => {
 *    // response { status: "SUCCESS" }
 * }).catch(err => {
 *    // errors
 * });
 *
 * @param caseID The insName (i.e., className!ruleName) of the Rule-UI-View record
 * @param viewID The insName (i.e., className!ruleName) of the Rule-UI-View record
 * @param fieldsConfig is an array of name and context properties
 * @returns The Promise that resolves to an object containing the values of the fields passed
 * @private
 */
const getCalculatedFields = (caseID: string, viewID: string, fieldsConfig: CalculatedFieldsConfig[]) => {
  const { restApi, reqType } = resolveRestApi(GET_CALCULATED_FIELDS_IN_VIEW, { caseID, viewID });

  return axiosFetch(restApi, {
    method: reqType,
    body: {
      calculations: {
        fields: fieldsConfig
      }
    }
  }).then((response: { data: CalculatedFieldsResponse }) => response.data);
};

export default {
  createViewRule,
  getViewRule,
  updateViewRule,
  deleteViewRule,
  getCalculatedFields
};
